import React, { useContext, useState, useMemo, useRef, useEffect } from "react";
import Context from "../../../Context/Context";
import { API } from "aws-amplify";
import { toast } from "react-toastify";
import { FaChevronDown } from "react-icons/fa";
import { createPortal } from "react-dom";
import PaginationComponent from "../../../common/Pagination";
import MembersTable from "./Components/MembersTable";
import MemberModal from "./Components/MemberModal";

// Custom Dropdown Component
const CustomDropdown = ({ 
  value, 
  onChange, 
  options, 
  placeholder, 
  isDarkMode, 
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const buttonRef = useRef(null);
  const portalRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        buttonRef.current && !buttonRef.current.contains(event.target) &&
        portalRef.current && !portalRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen && buttonRef.current) {
      document.addEventListener('mousedown', handleClickOutside);
      const rect = buttonRef.current.getBoundingClientRect();
      
      setPosition({
        top: rect.bottom + 4,
        left: Math.max(8, Math.min(rect.left, window.innerWidth - rect.width - 8)),
        width: rect.width,
      });
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const selectedOption = options.find(opt => opt.value === value) || null;
  const displayValue = selectedOption ? selectedOption.label : placeholder;

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full h-9 md:h-10 pl-3 md:pl-4 pr-8 md:pr-10 py-1.5 md:py-2 rounded-lg text-xs md:text-sm transition-all duration-200 text-left flex items-center justify-between ${
          isDarkMode 
            ? 'bg-[#1a1a1a] text-white hover:bg-[#222222] focus:outline-none focus:ring-2 focus:ring-[#FDCF08]' 
            : 'bg-white border border-gray-200 text-gray-900 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#FDCF08]'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
      >
        <span className="truncate">{displayValue}</span>
        <FaChevronDown 
          className={`ml-2 text-xs transition-transform duration-200 ${isOpen ? 'rotate-180' : ''} ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} 
        />
      </button>
      
      {isOpen && !disabled && createPortal(
        <div 
          ref={portalRef}
          className={`fixed z-[99999] rounded-lg shadow-xl overflow-hidden border ${
            isDarkMode ? 'bg-[#1a1a1a] border-[#2a2a2a]' : 'bg-white border-gray-200'
          }`}
          style={{
            top: `${position.top}px`,
            left: `${position.left}px`,
            width: `${position.width}px`,
          }}
        >
          <div className="max-h-60 overflow-y-auto">
            {options.length === 0 ? (
              <div className={`px-4 py-3 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                No options available
              </div>
            ) : (
              options.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => {
                    onChange(option.value);
                    setIsOpen(false);
                  }}
                  className={`w-full px-4 py-2.5 text-sm text-left transition-colors duration-150 ${
                    value === option.value
                      ? isDarkMode
                        ? 'bg-[#2a2a2a] text-[#FDCF08] font-medium'
                        : 'bg-[#FDCF08]/10 text-[#FDCF08] font-medium'
                      : isDarkMode
                      ? 'text-white hover:bg-[#2a2a2a]'
                      : 'text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  {option.label}
                </button>
              ))
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

const MembersPage = () => {
  const Ctx = useContext(Context);
  const UtilCtx = useContext(Context).util;
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("Active");
  const [searchQuery, setSearchQuery] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);

  const isDarkMode = Ctx.isDarkMode ?? true;
  const itemsPerPage = 5;

  // Filter members based on status and search
  const filteredMembers = useMemo(() => {
    let filtered = Ctx.userList || [];
    
    // Filter by status
    if (statusFilter && statusFilter !== "all") {
      filtered = filtered.filter(member => member.status === statusFilter);
    }
    
    // Filter by search query (name, email, or phone number)
    if (searchQuery) {
      filtered = filtered.filter(member => 
        (member.userName && member.userName.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (member.emailId && member.emailId.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (member.phoneNumber && member.phoneNumber.includes(searchQuery))
      );
    }
    
    return filtered.filter(member => !member.isArchived);
  }, [Ctx.userList, statusFilter, searchQuery]);

  const totalPages = Math.ceil(filteredMembers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedMembers = filteredMembers.slice(startIndex, endIndex);

  const handleMemberAction = (memberData, mode) => {
    setSelectedMember(memberData);
    setIsModalOpen(true);
  };

  const handleAddNewMember = () => {
    setSelectedMember(null);
    setIsModalOpen(true);
  };

  const handleSaveMember = async (formData) => {
    try {
      if (selectedMember) {
        // Edit existing member
        const updatedUser = await API.patch("user", `/admin/update-user`, {
          body: { ...formData, cognitoId: selectedMember.cognitoId },
        });
        // Update only the edited user in the list
        const newList = (Ctx.userList || []).map((user) =>
          user.cognitoId === updatedUser.cognitoId ? updatedUser : user
        );
        Ctx.setUserList(newList);
        toast.success("Member updated successfully");
      } else {
        // Create new member
        const newUser = await API.post("user", "/admin/create-member", {
          body: formData
        });
        Ctx.setUserList([...(Ctx.userList || []), newUser]);
        toast.success("Member created successfully");
      }
      setIsModalOpen(false);
    } catch (error) {
      toast.error(error.message || "Error saving member");
    }
  };

  return (
    <>
      <div className={`w-full md:w-[calc(100%-5rem)] mx-auto mt-0 md:mt-10 flex flex-col h-full transition-colors duration-200 ${isDarkMode ? 'bg-black' : 'bg-white'}`} style={{ fontFamily: "Inter, sans-serif" }}>
        {/* Header Section */}
        <div className={`w-full px-3 md:px-8 py-3 md:py-6 border-b ${isDarkMode ? 'border-[#2a2a2a]' : 'border-gray-200'}`}>
          <div className="flex flex-col gap-4">
            {/* Title */}
            <h1 className={`text-xl text-left md:text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-black'}`}>Members Management</h1>

            {/* Search Bar, Filter, and Add Button in One Row */}
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 md:gap-4 items-start sm:items-center justify-between">
              {/* Search Bar - Left Side */}
              <div className="w-full sm:w-64 md:w-72">
                <input
                  type="text"
                  placeholder="Search members..."
                  value={searchQuery}
                  onChange={(e) => {
                    setCurrentPage(1);
                    setSearchQuery(e.target.value);
                  }}
                  className={`w-full px-3 md:px-4 py-2 rounded-lg text-xs md:text-sm transition-all duration-200 outline-none ${
                    isDarkMode 
                      ? 'bg-[#1a1a1a] text-white placeholder-gray-400 focus:ring-2 focus:ring-[#FDCF08]'
                      : 'bg-white border border-gray-200 text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-[#FDCF08]'
                  }`}
                />
              </div>

              {/* Status Filter and Add Button - Right Side */}
              <div className="flex gap-2 sm:gap-3 md:gap-4 w-full sm:w-auto">
              {/* Status Filter */}
              <div className="w-full sm:w-48 md:w-56">
                <CustomDropdown
                  value={statusFilter}
                  onChange={(value) => {
                    setCurrentPage(1);
                    setStatusFilter(value);
                  }}
                  options={[
                    { value: "Active", label: "Active" },
                    { value: "InActive", label: "Inactive" },
                    { value: "all", label: "All" },
                  ]}
                  placeholder="Select Status"
                  isDarkMode={isDarkMode}
                />
              </div>

                {/* Add New Member Button */}
                <button
                  onClick={handleAddNewMember}
                  className="px-3 md:px-4 py-2 bg-[#FDCF08] text-black font-semibold rounded-lg flex items-center justify-center gap-1.5 md:gap-2 hover:bg-[#f0c000] transition-colors text-xs md:text-sm whitespace-nowrap"
                  style={{ fontFamily: "Inter, sans-serif" }}
                >
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8 3V13M3 8H13" stroke="black" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  <span>Add New Member</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto px-3 md:px-8 py-3 md:py-6">
          <MembersTable
            members={paginatedMembers}
            isDarkMode={isDarkMode}
            onAction={handleMemberAction}
          />

          <div className="mt-4 md:mt-6">
            <PaginationComponent
              showIcons={true}
              data={filteredMembers}
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              onPageChange={(page) => setCurrentPage(page)}
            />
          </div>
        </div>
      </div>

      {/* Member Modal */}
      <MemberModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedMember(null);
        }}
        memberData={selectedMember}
        isDarkMode={isDarkMode}
        onSave={handleSaveMember}
        readOnly={false}
      />
    </>
  );
};

export default MembersPage;
